import React, { useRef, useState } from "react";
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonCard, IonCardContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const getRandomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

const generateQuestion = () => {
    const num1 = getRandomNumber(2, 9);
    const num2 = getRandomNumber(1, num1 - 1); // Ensure num2 is smaller than num1
    const correctAnswer = num1 - num2;

    const options = new Set<number>();
    options.add(correctAnswer);
    while (options.size < 3) {
        options.add(getRandomNumber(0, 9));
    }

    return {
        question: `${num1} - ${num2} = ?`,
        correctAnswer,
        options: Array.from(options).sort(() => Math.random() - 0.5), // Shuffle options
    };
};

const SubtractionVisual: React.FC = () => {
    const [quiz, setQuiz] = useState(generateQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);
    document.title = "Visual Subtraction for UKG | Fun Interactive Math Activities";

    const handleAnswerClick = (option: number) => {
        if (option === quiz.correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setFeedback("")
            }, 5000)
        } else {
            setShowImage(false);
            setFeedback("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Explore visual subtraction for UKG kids using interactive objects and scenes. Download free worksheets and help children learn subtraction with ease." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/substraction/visual-substraction" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Visual Subtraction</h1>

                                    <p>
                                        Help your child master subtraction with <strong>Visual Subtraction</strong> activities. Perfect for UKG learners, these exercises use colorful visuals, objects, and scenes to teach subtraction as a real-life, hands-on experience. Kids see what is taken away — and what’s left behind — in a fun and engaging way.
                                    </p>

                                    <h2>🎯 What Is Visual Subtraction?</h2>
                                    <p>
                                        Visual subtraction is the process of subtracting numbers using pictures, drawings, or physical objects. This method allows children to <strong>see the subtraction process</strong> instead of just solving numbers on paper.
                                    </p>

                                    <h2>🧠 What Will Your Child Learn?</h2>
                                    <ul>
                                        <li>Understand subtraction as <strong>“taking away”</strong> or <strong>“removing”</strong></li>
                                        <li>Develop number sense and basic arithmetic skills</li>
                                        <li>Improve visual learning and observation</li>
                                        <li>Connect math to real-world scenarios</li>
                                    </ul>

                                    <h2>🔢 Example of Visual Subtraction</h2>
                                    <p>
                                        Imagine a picture of 6 birds on a branch. 2 birds fly away. How many are left?
                                        → <strong>4 birds remain</strong>.
                                    </p>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Smallest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Biggest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Number Comparison</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🏡 How to Practice at Home</h2>
                                    <ul>
                                        <li>Use toys, buttons, or snacks to act out subtraction</li>
                                        <li>Draw scenes (<strong>e.g., balloons or animals</strong>) and cross out some to subtract</li>
                                        <li>Make short stories involving subtraction (<strong>e.g., “You had 5 pencils, gave away 2…”</strong>)</li>
                                        <li>Let kids explain in their own words what is being subtracted</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Visual Subtraction</h1>

                                <p>
                                    Help your child master subtraction with <strong>Visual Subtraction</strong> activities. Perfect for UKG learners, these exercises use colorful visuals, objects, and scenes to teach subtraction as a real-life, hands-on experience. Kids see what is taken away — and what’s left behind — in a fun and engaging way.
                                </p>

                                <h2>🎯 What Is Visual Subtraction?</h2>
                                <p>
                                    Visual subtraction is the process of subtracting numbers using pictures, drawings, or physical objects. This method allows children to <strong>see the subtraction process</strong> instead of just solving numbers on paper.
                                </p>

                                <h2>🧠 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Understand subtraction as <strong>“taking away”</strong> or <strong>“removing”</strong></li>
                                    <li>Develop number sense and basic arithmetic skills</li>
                                    <li>Improve visual learning and observation</li>
                                    <li>Connect math to real-world scenarios</li>
                                </ul>

                                <h2>🔢 Example of Visual Subtraction</h2>
                                <p>
                                    Imagine a picture of 6 birds on a branch. 2 birds fly away. How many are left?
                                    → <strong>4 birds remain</strong>.
                                </p>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🏡 How to Practice at Home</h2>
                                <ul>
                                    <li>Use toys, buttons, or snacks to act out subtraction</li>
                                    <li>Draw scenes (<strong>e.g., balloons or animals</strong>) and cross out some to subtract</li>
                                    <li>Make short stories involving subtraction (<strong>e.g., “You had 5 pencils, gave away 2…”</strong>)</li>
                                    <li>Let kids explain in their own words what is being subtracted</li>
                                </ul>
                            </article>
                            <h2>{quiz.question}</h2>
                            <div style={{ marginBottom: "20px", display: 'flex', flexWrap: 'wrap' }}>
                                {quiz.options.map((option, index) => (
                                    <div className="background-blue" key={index} onClick={() => handleAnswerClick(option)} style={{ margin: "5px", padding: "10px", width: "60px" }}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            {feedback && <p>{feedback}</p>}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default SubtractionVisual;
